import { OptionTradingServiceStrategy } from './option-trading-service-strategy.service';

import { TransactionType, OrderType, Account, OrderDuration, UserType, OptionStrategy } from '../../models/common/trading-common.model';
import { AccountType, DeskRouteTimeZone, DeskRouteCode } from '../../models/common/trading-common.model';
import { TradingEntitlements } from '../../models/common/trading-entitlements';
import { TRADING_PLATFORM } from '../../utils/trading-constants';
import { Observable } from 'rxjs';
import { Injectable } from "@angular/core";

@Injectable()
export class RetailOptionTradingServiceStrategy extends OptionTradingServiceStrategy {

    private static SUPPORTED_PLATFORM = TRADING_PLATFORM.RETAIL;

    support(platform: string): boolean {
        return RetailOptionTradingServiceStrategy.SUPPORTED_PLATFORM === platform;
    }

    public accountTypes(account: Account, transactionType: TransactionType, symAccountType: AccountType): AccountType[] {
        const isMarginEnabledAccount: boolean = this.isMarginAcct(account);
        if (!!account && !!transactionType && !!symAccountType) { 
            if (transactionType === TransactionType.CLOSE_CALLS_OR_PUTS) {
                return [symAccountType];
            }
            if (transactionType === TransactionType.SELL_COVERED_CALLS) {
                return this.handleSellCoveredCalls(isMarginEnabledAccount);
            }
            return this.getDefaultAccountTypes(account);
        } else if (!!account && !symAccountType) {
            if ((transactionType === TransactionType.BUY_TO_OPEN || transactionType === TransactionType.SELL_TO_OPEN) && isMarginEnabledAccount) {
                return this.handleOpeningTransactions(transactionType);
            }
            return this.getDefaultAccountTypes(account);
        } else {
            return [AccountType.CASH];
        }
    }

     handleOpeningTransactions(transactionType: TransactionType) {
        if (transactionType === TransactionType.BUY_TO_OPEN) {
            return [AccountType.CASH, AccountType.MARGIN];
        } else if (transactionType === TransactionType.SELL_TO_OPEN) {
            return [AccountType.CASH, AccountType.SHORT];
        }
    }

    handleSellCoveredCalls(isMarginEnabledAccount: boolean) {
        if (isMarginEnabledAccount) {
            return [AccountType.SHORT];
        }  else {
            return [AccountType.CASH];
        }
    }

    private getDefaultAccountTypes(account: Account): AccountType[] {
        const isMarginEnabledAccount: boolean = this.isMarginAcct(account);
        if ( isMarginEnabledAccount ) {
            return [AccountType.CASH, AccountType.MARGIN];
        }
        return [AccountType.CASH];
    }

    isMarginAcct(account: Account) {
        return account?.marginEligibleInd === 'Y';
    }

    public getOptionStrategies(account: Account, entitlements: TradingEntitlements): string[] {
        // TODO -- Enhance with required account property checks
        const isMarginEnabledAccount: boolean = account.marginEligibleInd === 'Y';
        if (isMarginEnabledAccount) {
            return this.getMarginAccountOptionStrategies(entitlements);
        } else {
            return this.getNonMarginAccountOptionStrategies(entitlements);
        }
    }

    public getOptionMultiLegTransactionTypes(account: Account ,entitlements: TradingEntitlements, strategy: OptionStrategy): TransactionType[] {
        let transactionTypes: TransactionType[] = [];

        if (entitlements.isOptionBuySellToOpenEnabled) {
            if (strategy !== OptionStrategy.BUY_WRITE) {  // BUY_WRITE doesn't have transaction BUY_TO_OPEN
                transactionTypes.push(TransactionType.BUY_TO_OPEN);
            }

            if (entitlements.isOptionCloseCallOrPutsEnabled && strategy !== OptionStrategy.MARRIED_PUT) {
                transactionTypes.push(TransactionType.BUY_TO_CLOSE);
            }

            if (strategy !== OptionStrategy.MARRIED_PUT) {
                transactionTypes.push(TransactionType.SELL_TO_OPEN);
            }

            if (entitlements.isOptionCloseCallOrPutsEnabled && strategy !== OptionStrategy.BUY_WRITE) { // BUY_WRITE doesn't have transaction SELL_TO_CLOSE
                transactionTypes.push(TransactionType.SELL_TO_CLOSE);
            }
        }

        return transactionTypes;
    }

    public getTransactionTypes(account: Account ,entitlements: TradingEntitlements, isAPA: boolean): TransactionType[] {

        let transactionTypes: TransactionType[] = [];
        if(account.optionFunctionCode === "4")
        {
            transactionTypes.push(TransactionType.BUY_TO_OPEN);
            transactionTypes.push(TransactionType.SELL_TO_OPEN);
            transactionTypes.push(TransactionType.CLOSE_CALLS_OR_PUTS);
            transactionTypes.push(TransactionType.SELL_COVERED_CALLS);
        }
        else if(account.optionFunctionCode === "3")
        {
            transactionTypes.push(TransactionType.BUY_TO_OPEN);
            transactionTypes.push(TransactionType.CLOSE_CALLS_OR_PUTS);
            transactionTypes.push(TransactionType.SELL_COVERED_CALLS);
        }
        else if(account.optionFunctionCode === "2")
        {
            transactionTypes.push(TransactionType.BUY_TO_OPEN);
            transactionTypes.push(TransactionType.CLOSE_CALLS_OR_PUTS);
        }
        else if(account.optionFunctionCode === "1")
        {
            transactionTypes.push(TransactionType.CLOSE_CALLS_OR_PUTS);
            transactionTypes.push(TransactionType.SELL_COVERED_CALLS);
        }
        return transactionTypes;
    }

    public getOrderTypes(isNonDollar: boolean, qty: number): OrderType[] {
        let orderTypes: OrderType[] = [];

        orderTypes.push(OrderType.MARKET);
        orderTypes.push(OrderType.LIMIT);
        orderTypes.push(OrderType.AON);
        if(qty>1){
            orderTypes.push(OrderType.FOK);
        }
        orderTypes.push(OrderType.STOP_LIMIT);
        orderTypes.push(OrderType.STOP);

        return orderTypes;
    }

    public getOptionMultiLegOrderTypes(strategy: OptionStrategy, transactionType: TransactionType, modifier: any = {}): OrderType[] {
        console.info('Getting order types for multi-leg option strategy ' + strategy +
            ' using transaction type ' + transactionType);

        // Any strategy except "Single" will use this function to derive the proper Order Type options
        let orderTypes: OrderType[] = [];

        // All strategies can use Market
        orderTypes.push(OrderType.MARKET);

        switch ( strategy ) {
            case OptionStrategy.CALENDAR_SPREAD:
            case OptionStrategy.DIAGONAL_SPREAD:
            case OptionStrategy.VERTICAL_SPREAD:
            case OptionStrategy.COLLAR:
            case OptionStrategy.ROLL:
                orderTypes.push(OrderType.NET_CREDIT);
                orderTypes.push(OrderType.NET_DEBIT);
                orderTypes.push(OrderType.EVEN);
                break;
            case OptionStrategy.STRADDLE:
            case OptionStrategy.STRANGLE:
                this.pushStraddleStrangleOrderTypes(transactionType,orderTypes);

                break;
            case OptionStrategy.BUY_WRITE:
                this.pushBuyWriteOrderTypes(transactionType,orderTypes);

                break;
            case OptionStrategy.MARRIED_PUT:
                this.pushMarriedPutOrderTypes(transactionType,orderTypes);
                break;
            default:
        }

        // Additional modify -
        // GLOBOEM-5953 Restrict selection of Net Credit / Net Debit based on Net Bid and Net Ask
        let idx = orderTypes.indexOf(OrderType.NET_CREDIT);
        if ( !!modifier.dontShowNetCredit && idx > 0 ) {
            orderTypes.splice(idx, 1);
        }

        idx = orderTypes.indexOf(OrderType.NET_DEBIT);
        if ( !!modifier.dontShowNetDebit && idx > 0 ) {
            orderTypes.splice(idx, 1);
        }

        return orderTypes;
    }

    pushStraddleStrangleOrderTypes(transactionType:TransactionType, orderTypes:OrderType[]){
        if ( TransactionType.BUY_TO_OPEN === transactionType ) {
            orderTypes.push(OrderType.NET_DEBIT);
        } else if ( TransactionType.SELL_TO_OPEN === transactionType ) {
            orderTypes.push(OrderType.NET_CREDIT);
        } else {
            orderTypes.push(OrderType.NET_CREDIT);
            orderTypes.push(OrderType.NET_DEBIT);
            orderTypes.push(OrderType.EVEN);
        }
    }
    pushBuyWriteOrderTypes(transactionType:TransactionType, orderTypes:OrderType[]){
        if ( TransactionType.SELL_TO_OPEN === transactionType ) {
            orderTypes.push(OrderType.NET_CREDIT);
        } else if ( TransactionType.BUY_TO_CLOSE === transactionType ) {
            orderTypes.push(OrderType.NET_DEBIT);
        } else {
            console.error('Unexpected transaction type for Buy-Write order: ' + transactionType +
                '. Unable to determine valid Order Types.');
        }
    }

    pushMarriedPutOrderTypes(transactionType:TransactionType, orderTypes:OrderType[]){
        if ( TransactionType.BUY_TO_OPEN === transactionType ) {
            orderTypes.push(OrderType.NET_DEBIT);
        } else if ( TransactionType.SELL_TO_CLOSE === transactionType ) {
            orderTypes.push(OrderType.NET_CREDIT);
        } else {
            console.error('Unexpected transaction type for Married Put order: ' + transactionType +
                '. Unable to determine valid Order Types.');
        }
    }

    public getDurations(isMultiLegOrder: boolean, orderType?: string, ordQty?: number, userType?: UserType): OrderDuration[] {

        if (isMultiLegOrder || (orderType && orderType === OrderType.MARKET || orderType === OrderType.FOK)) {
            return [OrderDuration.DAY];
        } else {
            return [OrderDuration.DAY, OrderDuration.GTC];
        }
    }
    public  getDeskRouteTimeZone(): DeskRouteTimeZone[] {
        return [];
    }
    public  getDeskCode(): DeskRouteCode[] {
        return [];
    }

    public getCodeValuesExchangeList(): Observable<any> {
        throw new Error('Code values exchange list is not supported on the retail platform');
    }
}
